import type { CacheConfig, GraphQLResponse, GraphQLSingularResponse } from 'relay-runtime';

export const INCREMENTAL_ACCEPT_HEADER =
	'application/json, multipart/mixed, multipart/mixed; deferSpec=20220824';

type GraphQLIncrementalResponse = {
	incremental?: ReadonlyArray<GraphQLSingularResponse>;
	hasNext?: boolean;
};

export type GraphQLResponsePayload = GraphQLResponse | GraphQLIncrementalResponse;

type GraphQLResponseObserver = {
	next: (payload: GraphQLResponse) => void;
	error: (error: Error) => void;
};

// A request opts into the incremental (defer/multipart) transport by setting
// `cacheConfig.metadata.incremental`. This is the single signal that both the network router
// (getNetwork) and the batch fetcher key off. It MUST stay coupled to whether the query actually
// contains `@defer`: a `@defer` query sent without the multipart Accept header is rejected by AGG
// with `406 Not Acceptable`, so the call site choosing the `@defer` query variant is responsible for
// setting this flag whenever (and only when) it sends a `@defer` query.
export const isIncrementalRequest = (cacheConfig: CacheConfig): boolean =>
	cacheConfig.metadata?.incremental === true;

const getBoundary = (contentType: string): string | null => {
	const match = /boundary="?([^";]+)"?/i.exec(contentType);
	return match?.[1]?.trim() || null;
};

const getHeader = (response: Response, headerName: string): string => {
	return response.headers?.get(headerName) ?? '';
};

const isMultipartResponse = (response: Response): boolean =>
	getHeader(response, 'content-type').includes('multipart/mixed');

const getPartBody = (part: string): string => {
	const bodyParts = part.split(/\r?\n\r?\n/);
	return (bodyParts.length > 1 ? bodyParts.slice(1).join('\n\n') : part).trim();
};

const parseMultipartPart = (part: string): GraphQLResponsePayload | null => {
	const trimmedPart = part.trim();
	if (!trimmedPart || trimmedPart === '--' || trimmedPart.startsWith('--')) {
		return null;
	}

	const body = getPartBody(trimmedPart);
	return body ? (JSON.parse(body) as GraphQLResponsePayload) : null;
};

const extractMultipartPayloads = (
	chunk: string,
	boundaryString: string,
): { payloads: GraphQLResponsePayload[]; remainder: string } => {
	const parts = chunk.split(boundaryString);
	let incompletePart = '';
	const payloads: GraphQLResponsePayload[] = [];

	for (let index = 0; index < parts.length - 1; index++) {
		const part = parts[index];

		try {
			const payload = parseMultipartPart(incompletePart + part);
			if (payload) {
				payloads.push(payload);
				incompletePart = '';
			}
		} catch {
			incompletePart += part + boundaryString;
		}
	}

	return {
		payloads,
		remainder: incompletePart + parts[parts.length - 1],
	};
};

async function* readResponseBody(response: Response): AsyncGenerator<string> {
	const body = response.body as
		| AsyncIterable<Uint8Array | ArrayBuffer | string>
		| { getReader?: () => ReadableStreamDefaultReader<Uint8Array> }
		| null;

	if (!body) {
		throw new Error('Multipart response body is empty');
	}

	const decoder = new TextDecoder('utf-8');

	if (Symbol.asyncIterator in body) {
		for await (const chunk of body) {
			yield typeof chunk === 'string' ? chunk : decoder.decode(chunk, { stream: true });
		}
		const remainingText = decoder.decode();
		if (remainingText) {
			yield remainingText;
		}
		return;
	}

	if (typeof body.getReader === 'function') {
		const reader = body.getReader();
		try {
			while (true) {
				const { done, value } = await reader.read();
				if (done) {
					break;
				}
				yield decoder.decode(value, { stream: true });
			}
			const remainingText = decoder.decode();
			if (remainingText) {
				yield remainingText;
			}
		} finally {
			reader.releaseLock();
		}
		return;
	}

	throw new Error('Multipart response body is not readable');
}

async function* readMultipartPayloads(response: Response): AsyncGenerator<GraphQLResponsePayload> {
	const boundary = getBoundary(getHeader(response, 'content-type'));

	if (!boundary) {
		throw new Error('Invalid multipart boundary');
	}

	const boundaryString = `--${boundary}`;
	let remainder = '';

	for await (const text of readResponseBody(response)) {
		const result = extractMultipartPayloads(remainder + text, boundaryString);
		remainder = result.remainder;

		yield* result.payloads;
	}

	const trailingPart = remainder.trim();
	if (!trailingPart || trailingPart === '--') {
		return;
	}

	try {
		const payload = parseMultipartPart(trailingPart.replace(/--$/, ''));
		if (payload) {
			yield payload;
		}
	} catch {
		throw new Error('Malformed multipart response chunk');
	}
}

export async function* readGraphQLResponsePayloads(
	response: Response,
): AsyncGenerator<GraphQLResponsePayload> {
	if (isMultipartResponse(response)) {
		yield* readMultipartPayloads(response);
		return;
	}

	yield (await response.json()) as GraphQLResponsePayload;
}

export const emitRelayPayload = (
	payload: GraphQLResponsePayload,
	observer: GraphQLResponseObserver,
): boolean => {
	if (!Array.isArray(payload) && 'incremental' in payload && Array.isArray(payload.incremental)) {
		if (payload.incremental.length > 0) {
			observer.next(payload.incremental);
		} else if (payload.hasNext === false) {
			observer.next({
				data: null,
				extensions: { is_final: true },
			});
		}
		return payload.hasNext === false;
	}

	observer.next(payload as GraphQLResponse);
	return !Array.isArray(payload) && 'hasNext' in payload && payload.hasNext === false;
};
